import React, { useState, useEffect, useRef } from 'react';
import { evolutionAPI } from '../api';
import { QUICK_REPLIES } from '../config';

export default function ChatWindow({ contact, messages, onSend, companies, instances }) {
  const [input, setInput] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!contact) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#141417', color: '#52525b', fontSize: 14 }}>
        Selecione uma conversa
      </div>
    );
  }

  const company = companies.find(c => c.id === contact.company);
  const instance = instances.find(i => i.id === contact.instance);
  const getInitials = name => name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      // Envia via Evolution API
      await evolutionAPI.sendText(instance?.name || '', contact.phone, input.trim());
      onSend(contact.id, input.trim());
      setInput('');
    } catch (e) {
      // Em dev: envia localmente mesmo sem API
      onSend(contact.id, input.trim());
      setInput('');
    }
    setSending(false);
  };

  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const insertQuickReply = (text) => {
    setInput(text);
    setShowQR(false);
  };

  const s = {
    container: { flex: 1, display: 'flex', flexDirection: 'column', background: '#141417', overflow: 'hidden' },
    header: {
      background: '#1c1c1f',
      borderBottom: '1px solid #27272a',
      padding: '10px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    },
    avatar: {
      width: 36, height: 36,
      borderRadius: '50%',
      background: (company?.color || '#6366f1') + '33',
      color: company?.color || '#6366f1',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 600, flexShrink: 0,
    },
    headerInfo: { flex: 1 },
    hName: { fontSize: 14, fontWeight: 500, color: '#e4e4e7' },
    hSub: { fontSize: 11, color: '#71717a', marginTop: 1, display: 'flex', alignItems: 'center', gap: 6 },
    onlineDot: { width: 7, height: 7, background: '#22c55e', borderRadius: '50%', display: 'inline-block' },
    hActions: { display: 'flex', gap: 4 },
    iconBtn: {
      width: 32, height: 32,
      background: 'transparent',
      border: 'none',
      borderRadius: 8,
      color: '#71717a',
      fontSize: 16,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer',
    },
    messages: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    dateSep: {
      textAlign: 'center',
      fontSize: 11,
      color: '#52525b',
      padding: '4px 0 8px',
    },
    systemMsg: {
      background: '#1c1c1f',
      border: '1px solid #27272a',
      borderRadius: 8,
      padding: '6px 12px',
      fontSize: 11,
      color: '#71717a',
      textAlign: 'center',
      maxWidth: 400,
      margin: '0 auto',
    },
    bubbleRow: (fromMe) => ({
      display: 'flex',
      justifyContent: fromMe ? 'flex-end' : 'flex-start',
      gap: 8,
      maxWidth: '75%',
      alignSelf: fromMe ? 'flex-end' : 'flex-start',
    }),
    bubble: (fromMe) => ({
      padding: '8px 12px',
      borderRadius: fromMe ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
      background: fromMe ? '#6366f1' : (contact.agentActive ? '#1e3a2f' : '#27272a'),
      color: fromMe ? '#fff' : '#e4e4e7',
      fontSize: 13,
      lineHeight: 1.5,
      maxWidth: '100%',
      wordBreak: 'break-word',
      border: fromMe ? 'none' : (contact.agentActive ? '1px solid #166534' : '1px solid #3f3f46'),
    }),
    msgTime: {
      fontSize: 10,
      opacity: 0.6,
      textAlign: 'right',
      marginTop: 3,
    },
    botLabel: {
      fontSize: 9,
      color: '#22c55e',
      marginBottom: 2,
    },
    inputArea: {
      background: '#1c1c1f',
      borderTop: '1px solid #27272a',
      padding: '10px 14px',
    },
    toolbar: {
      display: 'flex',
      gap: 4,
      marginBottom: 8,
    },
    toolBtn: {
      padding: '4px 10px',
      background: 'transparent',
      border: '1px solid #3f3f46',
      borderRadius: 6,
      color: '#71717a',
      fontSize: 11,
      cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 4,
    },
    inputRow: { display: 'flex', gap: 8, alignItems: 'flex-end' },
    textarea: {
      flex: 1,
      minHeight: 36,
      maxHeight: 100,
      padding: '8px 12px',
      background: '#27272a',
      border: '1px solid #3f3f46',
      borderRadius: 10,
      color: '#e4e4e7',
      fontSize: 13,
      resize: 'none',
      outline: 'none',
    },
    sendBtn: {
      width: 36, height: 36,
      background: '#6366f1',
      border: 'none',
      borderRadius: 10,
      color: '#fff',
      fontSize: 16,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
      opacity: sending ? 0.6 : 1,
    },
    qrPanel: {
      position: 'absolute',
      bottom: 80,
      left: 0,
      right: 0,
      background: '#1c1c1f',
      border: '1px solid #27272a',
      borderRadius: 10,
      padding: 12,
      zIndex: 10,
    },
    qrItem: {
      padding: '8px 10px',
      borderRadius: 6,
      cursor: 'pointer',
      fontSize: 12,
      color: '#a1a1aa',
      borderBottom: '1px solid #27272a',
      transition: 'background 0.1s',
    },
  };

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.avatar}>{getInitials(contact.name)}</div>
        <div style={s.headerInfo}>
          <div style={s.hName}>{contact.name}</div>
          <div style={s.hSub}>
            <span style={s.onlineDot} />
            {instance?.name} · {contact.phone}
            {contact.agentActive && <span style={{ color: '#22c55e', fontWeight: 600 }}>· 🤖 Bot ativo</span>}
          </div>
        </div>
        <div style={s.hActions}>
          <button style={s.iconBtn} title="Buscar">🔍</button>
          <button style={s.iconBtn} title="Transferir">↔️</button>
          <button style={s.iconBtn} title="Mais">⋯</button>
        </div>
      </div>

      {/* Messages */}
      <div style={s.messages}>
        <div style={s.dateSep}>Hoje</div>
        {messages.map(msg => {
          if (msg.type === 'system') {
            return (
              <div key={msg.id} style={s.systemMsg}>
                🤖 {msg.text}
              </div>
            );
          }
          const fromMe = msg.from === 'me' || msg.from === 'bot';
          const isBot = msg.from === 'bot';
          return (
            <div key={msg.id} style={s.bubbleRow(fromMe)}>
              <div>
                {isBot && <div style={s.botLabel}>🤖 Sofia (IA)</div>}
                <div style={s.bubble(fromMe)}>
                  {msg.text}
                  <div style={s.msgTime}>
                    {msg.time}
                    {fromMe && ' ✓✓'}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div style={{ ...s.inputArea, position: 'relative' }}>
        {showQR && (
          <div style={s.qrPanel}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#71717a', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Respostas rápidas
            </div>
            {QUICK_REPLIES.map(qr => (
              <div
                key={qr.id}
                style={s.qrItem}
                onClick={() => insertQuickReply(qr.text)}
                onMouseEnter={e => e.currentTarget.style.background = '#27272a'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <strong style={{ color: '#e4e4e7', display: 'block', fontSize: 11 }}>{qr.title}</strong>
                {qr.text.substring(0, 60)}...
              </div>
            ))}
          </div>
        )}
        <div style={s.toolbar}>
          <button style={s.toolBtn}>📎 Arquivo</button>
          <button style={s.toolBtn}>🖼️ Mídia</button>
          <button
            style={{ ...s.toolBtn, color: showQR ? '#818cf8' : '#71717a' }}
            onClick={() => setShowQR(!showQR)}
          >
            ⚡ Respostas
          </button>
          <button style={{ ...s.toolBtn, marginLeft: 'auto' }}>⏰ Agendar</button>
        </div>
        <div style={s.inputRow}>
          <textarea
            style={s.textarea}
            placeholder={contact.agentActive ? 'Bot ativo — escreva para assumir conversa...' : 'Digite uma mensagem...'}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            rows={1}
          />
          <button style={s.sendBtn} onClick={handleSend} disabled={sending}>
            ➤
          </button>
        </div>
      </div>
    </div>
  );
}
